import { Box, Card, CardContent, Typography } from "@mui/material";
import type {
  ChangelogEntry,
  ChangelogRepo,
  ChangelogSince,
  ChangelogType,
} from "@xop/dashboard-shared";
import { CHANGELOG_REPOS, CHANGELOG_TYPES, groupByDay } from "@xop/dashboard-shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Pagination } from "../../../components/ui/pagination.js";
import { tokenStore } from "../../../lib/tokenStore.js";
import { changelogApi, type ChangelogParams } from "./api.js";
import { ChangelogCalendar } from "./ChangelogCalendar.js";
import { ChangelogEventList } from "./ChangelogEventList.js";
import { monthStr, shiftMonth } from "./dateHelpers.js";
import { TYPE_META, repoLabel } from "./meta.js";
import { TypeChip } from "./TypeChip.js";
import { WhatsNewBanner } from "./WhatsNewBanner.js";

/**
 * System Admin ▸ Change Log (`/admin/changelog`).
 *
 * Two modes:
 *  - **Calendar mode** (default): event list LEFT (larger) + a compact
 *    month calendar RIGHT that navigates/filters by day.
 *  - **"What's new" mode** (`?sinceVersion=0.1.7` / `?since=2026-05-01`):
 *    a deep-link, typically opened after a desktop upgrade, that shows
 *    everything shipped across the stack since that version's release — a
 *    full-width banner + event list (no calendar; the range spans months).
 *
 * The whole window is fetched once; type/repo/day filtering is client-side
 * via the shared `groupByDay`. Read-only — entries are authored by
 * committing to the server's `data/changelog.ts` (see `docs/changelog.md`).
 *
 * No own <PageHeader> — the shared "System Admin" header + tab strip live
 * in AdminLayout.tsx (same contract as the other tabs).
 */

/** House list convention: 25 rows/page through the shared <Pagination>. */
const PER_PAGE = 25;

export interface ChangelogPanelProps {
  /** Test seam — the month to open on. Defaults to the current month. */
  initialMonth?: string;
  /** Test seam — "today" for the calendar highlight. */
  today?: Date;
}

export function ChangelogPanel({
  initialMonth,
  today,
}: ChangelogPanelProps = {}): JSX.Element {
  const accessToken = tokenStore.getAccess();
  const authHeader = accessToken ? `Bearer ${accessToken}` : undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const sinceVersion = searchParams.get("sinceVersion") || undefined;
  const sinceDate = searchParams.get("since") || undefined;
  const sinceMode = Boolean(sinceVersion || sinceDate);

  const [month, setMonth] = useState(initialMonth ?? monthStr(today ?? new Date()));
  const [repo, setRepo] = useState<"" | ChangelogRepo>("");
  const [types, setTypes] = useState<Set<ChangelogType>>(new Set());
  const [focusedDay, setFocusedDay] = useState<string | null>(null);
  const [allEntries, setAllEntries] = useState<ChangelogEntry[]>([]);
  const [sinceInfo, setSinceInfo] = useState<ChangelogSince | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ChangelogParams = sinceVersion
        ? { sinceVersion }
        : sinceDate
          ? { since: sinceDate }
          : { month };
      const data = await changelogApi.list(params, authHeader);
      setAllEntries(data.entries);
      setSinceInfo(sinceMode ? (data.since ?? null) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setAllEntries([]);
      setSinceInfo(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader, month, sinceMode, sinceVersion, sinceDate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(
    () =>
      allEntries.filter(
        (e) =>
          (types.size === 0 || types.has(e.type)) &&
          (!repo || e.repo === repo),
      ),
    [allEntries, types, repo],
  );
  const days = useMemo(() => groupByDay(filtered), [filtered]);
  const eventDays = useMemo(
    () =>
      !sinceMode && focusedDay
        ? days.filter((d) => d.date === focusedDay)
        : days,
    [days, focusedDay, sinceMode],
  );

  // Per-type counts for the filter chips, scoped to the selected repo (but
  // NOT to the type selection itself — selecting one type must not zero the
  // others, or you couldn't see what's left to add). Recomputes on repo
  // change so the chips always reflect the repo currently in view.
  const repoScoped = useMemo(
    () => (repo ? allEntries.filter((e) => e.repo === repo) : allEntries),
    [allEntries, repo],
  );
  const typeCounts = useMemo(() => {
    const counts = Object.fromEntries(
      CHANGELOG_TYPES.map((t) => [t, 0]),
    ) as Record<ChangelogType, number>;
    for (const e of repoScoped) counts[e.type] += 1;
    return counts;
  }, [repoScoped]);

  // Drop a stranded focused day after a filter change (calendar mode only).
  useEffect(() => {
    if (focusedDay && !days.some((d) => d.date === focusedDay)) {
      setFocusedDay(null);
    }
  }, [days, focusedDay]);

  // Paginate the visible events at 25/page (house convention), keeping the
  // day grouping: flatten the current view (newest-first), slice the page,
  // then re-group that slice for display.
  const flatEntries = useMemo(() => eventDays.flatMap((d) => d.entries), [eventDays]);
  const totalCount = flatEntries.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  // True per-day totals for the current view (pre-pagination). A page slice
  // can hold only part of a busy day, so the re-grouped slice's own count is
  // wrong for the header — restore the real total (and type set) here so the
  // day header reads, e.g., "30 updates" and agrees with the calendar marker.
  // Pagination only limits how many entry *cards* render, never the headline.
  const dayTotals = useMemo(
    () => new Map(eventDays.map((d) => [d.date, d] as const)),
    [eventDays],
  );
  const pagedDays = useMemo(
    () =>
      groupByDay(
        flatEntries.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
      ).map((d) => {
        const full = dayTotals.get(d.date);
        return full ? { ...d, count: full.count, types: full.types } : d;
      }),
    [flatEntries, safePage, dayTotals],
  );

  // Reset to the first page whenever the view changes.
  const typesKey = useMemo(() => [...types].sort().join("|"), [types]);
  useEffect(() => {
    setPage(1);
  }, [month, repo, typesKey, focusedDay, sinceMode, sinceVersion, sinceDate]);

  const toggleType = useCallback((t: ChangelogType) => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }, []);

  const onSelectDay = useCallback(
    (date: string) => setFocusedDay((prev) => (prev === date ? null : date)),
    [],
  );

  const exitSinceMode = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const monthTotal = allEntries.length;
  const filteredTotal = filtered.length;
  const filtersActive = types.size > 0 || Boolean(repo);
  const summary = loading
    ? "Loading…"
    : sinceMode
      ? `${filteredTotal} update${filteredTotal === 1 ? "" : "s"}`
      : filtersActive
        ? `${filteredTotal} of ${monthTotal} update${monthTotal === 1 ? "" : "s"} this month`
        : `${monthTotal} update${monthTotal === 1 ? "" : "s"} this month`;

  // Event list (current page) + the shared pagination bar — same in both modes.
  const listSection = (
    <Box sx={{ display: "grid", gap: 2 }}>
      <ChangelogEventList
        days={pagedDays}
        focusedDay={sinceMode ? null : focusedDay}
        onClearFocus={() => setFocusedDay(null)}
        loading={loading}
      />
      <Pagination
        page={safePage}
        perPage={PER_PAGE}
        totalCount={totalCount}
        onPageChange={setPage}
        entityNoun="updates"
        hideIfSinglePage
      />
    </Box>
  );

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {/* Controls: repo + summary, then the type filter (both modes) */}
      <Box sx={{ display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <Typography
            component="label"
            sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14, color: "text.disabled" }}
          >
            Repo
            {/* Native <select> (not MUI Select) so the filter test can drive it
                via fireEvent.change on the aria-labelled control. */}
            <Box
              component="select"
              aria-label="Filter by repo"
              value={repo}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setRepo(e.target.value as "" | ChangelogRepo)
              }
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.primary",
                px: 1.5,
                py: 0.75,
                fontSize: 14,
                cursor: "pointer",
                "&:hover": { borderColor: "text.disabled" },
              }}
            >
              <option value="">All repos</option>
              {CHANGELOG_REPOS.map((r) => (
                <option key={r} value={r}>
                  {repoLabel(r)}
                </option>
              ))}
            </Box>
          </Typography>
          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
            {summary}
          </Typography>
        </Box>

        <Box
          role="group"
          aria-label="Filter by change type"
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
        >
          <TypeChip
            label="All types"
            active={types.size === 0}
            onClick={() => setTypes(new Set())}
          />
          {CHANGELOG_TYPES.map((t) => (
            <TypeChip
              key={t}
              label={TYPE_META[t].label}
              dot={TYPE_META[t].dot}
              count={typeCounts[t]}
              active={types.has(t)}
              onClick={() => toggleType(t)}
            />
          ))}
        </Box>
      </Box>

      {error && (
        <Box
          role="alert"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            px: 1.5,
            py: 1,
            fontSize: 14,
            color: "error.main",
          }}
        >
          {error}
        </Box>
      )}

      {sinceMode ? (
        /* "What's new since you upgraded" — full-width list, no calendar. */
        <Box sx={{ display: "grid", gap: 2 }}>
          <WhatsNewBanner
            since={sinceInfo}
            count={totalCount}
            loading={loading}
            onExit={exitSinceMode}
          />
          {listSection}
        </Box>
      ) : (
        /* Calendar mode — event list (larger) + compact calendar (smaller). */
        <Box
          sx={{
            display: "grid",
            gap: 2,
            // `minmax(0, …)` on the list track + `minWidth: 0` on the calendar
            // wrapper keep both columns from overflowing the row; the calendar
            // column is sized to comfortably hold the seven day-cells.
            gridTemplateColumns: { xl: "minmax(0, 1fr) 21rem" },
          }}
        >
          {listSection}
          <Box sx={{ alignSelf: "start", minWidth: 0, position: { xl: "sticky" }, top: { xl: 16 } }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <ChangelogCalendar
                  compact
                  month={month}
                  days={days}
                  selectedDate={focusedDay}
                  onSelectDate={onSelectDay}
                  onPrevMonth={() => setMonth((m) => shiftMonth(m, -1))}
                  onNextMonth={() => setMonth((m) => shiftMonth(m, 1))}
                  onToday={() => setMonth(monthStr(today ?? new Date()))}
                  {...(today ? { today } : {})}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
}
